import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, MessageSquareWarning } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { useSettings } from "@/providers/SettingsProvider";
import { useMessages } from "@/hooks/inbox/useMessages";
import { useConversationContext } from "@/hooks/inbox/useConversationContext";
import { ThreadHeader } from "./ThreadHeader";
import { Composer } from "./Composer";
import { DaySeparator } from "./DaySeparator";
import { ChatMessageGroup } from "./ChatMessageGroup";
import { EmailMessageCard } from "./EmailMessageCard";
import { groupByDay, groupConsecutiveBySender, sortMessagesAscending } from "./utils";
import type { InboxConversation, SaveEntity } from "@/types/inbox";

const ThreadSkeleton: React.FC<{ isEmail: boolean }> = ({ isEmail }) =>
	isEmail ? (
		<div className="mx-auto w-full max-w-3xl space-y-3 p-4" aria-hidden>
			{Array.from({ length: 2 }).map((_, index) => (
				<div key={index} className="rounded-sm border border-border bg-card p-4">
					<div className="flex items-center gap-3">
						<Skeleton className="h-9 w-9 rounded-sm" />
						<div className="flex-1 space-y-2">
							<Skeleton className="h-3.5 w-1/3" />
							<Skeleton className="h-3 w-1/2" />
						</div>
					</div>
					{index === 1 && (
						<div className="mt-4 space-y-2">
							<Skeleton className="h-3 w-full" />
							<Skeleton className="h-3 w-5/6" />
							<Skeleton className="h-3 w-2/3" />
						</div>
					)}
				</div>
			))}
		</div>
	) : (
		<div className="space-y-4 p-4" aria-hidden>
			{[0, 1, 2, 3].map((index) => (
				<div key={index} className={cn("flex", index % 2 ? "justify-end" : "justify-start")}>
					<Skeleton className={cn("h-10 rounded-md", index % 2 ? "w-2/5" : "w-1/2")} />
				</div>
			))}
		</div>
	);

interface MessageThreadProps {
	conversation: InboxConversation;
	toolbarId: string;
	onBack?: () => void;
	contextPanelOpen?: boolean;
	onToggleContextPanel?: () => void;
	/** Host entity to pre-link message saves to (entity mailbox tabs). */
	currentEntity?: SaveEntity;
}

/** Center pane: header, day-grouped messages, composer. */
export const MessageThread: React.FC<MessageThreadProps> = ({
	conversation,
	toolbarId,
	onBack,
	contextPanelOpen,
	onToggleContextPanel,
	currentEntity,
}) => {
	const { t } = useTranslation();
	const settings = useSettings();
	const isDarkMode =
		settings.colorMode === "dark" ||
		(settings.colorMode === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);

	const { data: messages, isLoading, isError } = useMessages(conversation);
	const { channel, participants, participantsById, matchedCandidate } =
		useConversationContext(conversation);
	const isEmail = channel === "email";

	const sorted = useMemo(() => sortMessagesAscending(messages || []), [messages]);
	const days = useMemo(() => groupByDay(sorted), [sorted]);
	const lastMessageId = sorted[sorted.length - 1]?.id;

	const scrollRef = useRef<HTMLDivElement>(null);
	const [nearBottom, setNearBottom] = useState(true);
	const nearBottomRef = useRef(true);

	const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
		const el = scrollRef.current;
		if (el) el.scrollTo({ top: el.scrollHeight, behavior });
	}, []);

	const handleScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		const isNear = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
		nearBottomRef.current = isNear;
		setNearBottom(isNear);
	}, []);

	// Jump to the latest message when opening a conversation…
	useEffect(() => {
		scrollToBottom();
		nearBottomRef.current = true;
		setNearBottom(true);
	}, [conversation.id, isLoading, scrollToBottom]);

	// …and follow new messages unless the user scrolled up to read history.
	useEffect(() => {
		if (nearBottomRef.current) scrollToBottom("smooth");
	}, [lastMessageId, scrollToBottom]);

	return (
		<div className="flex h-full min-h-0 flex-col bg-background">
			<ThreadHeader
				conversation={conversation}
				participants={participants}
				matchedCandidate={matchedCandidate}
				onBack={onBack}
				contextPanelOpen={contextPanelOpen}
				onToggleContextPanel={onToggleContextPanel}
			/>

			<div className="relative min-h-0 flex-1">
				<div
					ref={scrollRef}
					onScroll={handleScroll}
					className={cn(
						"absolute inset-0 overflow-y-auto scrollbar-thin",
						isEmail ? "bg-muted/30" : "bg-background"
					)}
				>
					{isLoading ? (
						<ThreadSkeleton isEmail={isEmail} />
					) : isError ? (
						<EmptyState
							icon={MessageSquareWarning}
							title={t("inbox.errors.messagesTitle", "Couldn't load messages")}
							description={t(
								"inbox.errors.messagesDescription",
								"Something went wrong while loading this conversation. Try again in a moment."
							)}
							className="h-full"
						/>
					) : isEmail ? (
						<div className="mx-auto w-full max-w-3xl space-y-3 p-4">
							{days.map((day) => (
								<React.Fragment key={day.date.toISOString()}>
									<DaySeparator date={day.date} />
									{day.items.map((message, index) => {
										const isLatestOfThread =
											message.id === lastMessageId ||
											(message.optimistic ?? false);
										return (
											<EmailMessageCard
												key={message.id}
												message={message}
												conversation={conversation}
												participantsById={participantsById}
												isDarkMode={isDarkMode}
												defaultExpanded={isLatestOfThread || sorted.length <= 2}
												matchedCandidate={matchedCandidate}
												currentEntity={currentEntity}
											/>
										);
									})}
								</React.Fragment>
							))}
						</div>
					) : (
						<div className="space-y-4 p-4">
							{days.map((day) => (
								<React.Fragment key={day.date.toISOString()}>
									<DaySeparator date={day.date} />
									<div className="space-y-4">
										{groupConsecutiveBySender(day.items).map((group) => (
											<ChatMessageGroup
												key={`${group.senderAttendeeId}-${group.messages[0]?.id}`}
												group={group}
												conversation={conversation}
												participantsById={participantsById}
												matchedCandidate={matchedCandidate}
												currentEntity={currentEntity}
											/>
										))}
									</div>
								</React.Fragment>
							))}
						</div>
					)}
				</div>

				{!nearBottom && sorted.length > 0 && (
					<Button
						variant="outline"
						size="icon"
						className="absolute bottom-4 right-4 h-8 w-8 rounded-full shadow-card-hover animate-fade-in"
						onClick={() => scrollToBottom("smooth")}
						aria-label={t("inbox.jumpToLatest", "Jump to latest")}
					>
						<ArrowDown className="h-4 w-4" />
					</Button>
				)}
			</div>

			<Composer conversation={conversation} toolbarId={toolbarId} />
		</div>
	);
};
